import { Container } from 'components/Container/Container'
import { useContractPageContext } from './Providers/ContractPageContextProvider'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Menu } from '../../components/Menu/Menu'
import { useLocation, useParams } from 'react-router-dom'
import {
  buildContractDetailsPath,
  isContractUuid,
  safeString
} from '../../helper/common'
import useRouter from '../../hooks/useRouter'
import { getUser } from '../../redux/store/user/getters'
import ContractPageRouter from './ContractPageRouter'
import { routes } from '../../router'
import { contractPageRoute } from './constants'

const ContractsContainer = () => {
  const user = useSelector(getUser)
  const { state, actions } = useContractPageContext()
  const { push } = useRouter()
  const { pathname } = useLocation()
  const { isCanSeeMyContracts, activeTab, tabs } = state
  const { setActiveTab } = actions
  const { type: routeType } = useParams()

  const RenderedMenu = useMemo(() => {
    const handleSelectTab = (e) => {
      setActiveTab(e.key)
      push(routes.contracts + '/' + e.key)
    }

    return (
      <Menu
        mode="horizontal"
        onSelect={handleSelectTab}
        selectedKeys={[activeTab]}
        defaultSelectedKeys={[activeTab]}
        items={tabs}
      />
    )
  }, [activeTab, push, setActiveTab, tabs])

  // Keep contracts list URL in sync with the active menu tab.
  // For "My contracts" users, also rewrite legacy/deep-link paths to valid routes.
  useEffect(() => {
    if (!isCanSeeMyContracts) {
      // Other roles: /contracts/:type is a real tab (e.g. central_repository), not a contract id.
      if (routeType?.length) {
        setActiveTab(safeString(routeType))
      } else {
        setActiveTab(contractPageRoute.contracts)
      }
      return
    }

    // Email/notifications often link to /contracts/{contractId}, but these users
    // must open details at /contracts/my_contracts/{contractId} or the page breaks.
    if (isContractUuid(routeType)) {
      const contractDetailsPath = buildContractDetailsPath(user.role, routeType)
      if (pathname !== contractDetailsPath) {
        push(contractDetailsPath, { replace: true })
      }
      return
    }

    // Default landing: bare /contracts → /contracts/my_contracts (their only list tab).
    if (!routeType && pathname === routes.contracts) {
      push(`${routes.contracts}${routes.myContracts}`, { replace: true })
      return
    }

    // Valid tab segment (my_contracts, central_repository, etc.) — highlight matching menu item.
    setActiveTab(safeString(routeType || contractPageRoute.contracts))
  }, [isCanSeeMyContracts, pathname, push, routeType, setActiveTab, user.role])

  return (
    <Container
      title="Contracts"
      menu={!isCanSeeMyContracts ? null : RenderedMenu}
    >
      <ContractPageRouter />
    </Container>
  )
}

export default ContractsContainer
